import { useState } from 'react';
import { ButtonGreen, ButtonGrey, ButtonRed } from '../../components/shared/Buttons';
import { Input } from '../../components/shared/Input';
import { FormRow, InputCol, InputRow } from '../../components/shared/InputRow';
import { Modal, ModalProps } from '../../components/shared/Modal';
import {
	checkCurrencyFormatFull,
	checkCurrencyFormatPartly,
	checkDateSchemaFormatFull,
	checkDateSchemaFormatPartly,
	convertEnumsToOptions,
	getOptionList,
} from '../../scripts/scripts';
import { Select } from '../../components/shared/Select';
import {
	TixBankTypes,
	TixBought,
	TixCurrencyTypes,
	TixEvent,
	TixOrder,
	TixPrimaryTypes,
	TixRegionTypes,
} from '../../types/TixTypes';
import toast from 'react-hot-toast';
import BigNumber from 'bignumber.js';
import { EditOrderPayload, GETEventResponseBase } from '../../types/TixAPITypes';
import _ from 'lodash';
import { useApiMutation } from '../../scripts/api/api_v2';
import { EditOrderAPIFunc } from '../../scripts/hooks/useEditOrderAPI';
import { FlexCol } from '../shared/Flex';
import styled from 'styled-components';
import { GridRow } from '../shared/Grid';
import { Text } from '../shared/Text';
import { Checkbox } from '../shared/Checkbox';

const StyledDivider = styled.div`
	width: 1px;
	height: 100%;
	background-color: ${({ theme }) => theme.border};
`;

const StyledSeatRow = styled(FormRow)`
	border-bottom: 1px solid ${({ theme }) => theme.border};
	padding-bottom: 1.5rem;
	&:last-of-type {
		border-bottom: none;
	}
`;

const StyledSeatInfoContainer = styled(FlexCol)``;

const StyledSeatPropsContainer = styled(FlexCol)`
	row-gap: 1rem;
`;

export type OnClickEditOrderModal = (data: { event: GETEventResponseBase; orderId: TixOrder['id'] }) => void;

export function ModalEditOrder({
	closeModal,
	orderId,
	event,
	editOrderAPI,
	onOrderDeleted,
}: ModalProps & {
	orderId: TixOrder['id'];
	event: GETEventResponseBase;
	editOrderAPI: EditOrderAPIFunc;
	onOrderDeleted: (eventId: TixEvent['id'], orderId: TixOrder['id'], seatIds: TixBought['id'][]) => void;
}) {
	const order = event.orders[orderId];
	const [saving, setSaving] = useState<boolean>(false);
	const [eventState, setEventState] = useState({
		event: event.event,
		date: event.date,
		city: event.city,
		region: event.region,
	});
	const [orderState, setOrderState] = useState<{
		email: string;
		reference: string;
		primary: string;
		paymentDate: string;
		paymentSplits: TixOrder['paymentSplits'];
		paymentNative: TixOrder['paymentNative'];
		paymentNativeCurrency: TixOrder['paymentNativeCurrency'];
	}>({
		email: order.email,
		reference: order.reference,
		primary: order.primary,
		paymentSplits: order.paymentSplits,
		paymentDate: order.paymentDate,
		paymentNative: order.paymentNative,
		paymentNativeCurrency: order.paymentNativeCurrency,
	});
	const [seatsState, setSeatsState] = useState<EditOrderPayload['seats']>(
		Object.values(event.seats).filter((seat) => seat.orderId === orderId),
	);
	const [deletedSeats, setDeletedSeats] = useState<TixBought['id'][]>([]);

	const { fetchData: deleteOrderAPI } = useApiMutation('tix.delete-order');

	const onChangeEvent = (name: keyof typeof eventState, value: string | number) => {
		setEventState((state) => ({ ...state, [name]: value }));
	};

	const onChangeOrder = (name: keyof typeof orderState, value: string | number) => {
		setOrderState((state) => ({ ...state, [name]: value }));
	};

	const addPaymentSplit = () => {
		setOrderState((state) => ({
			...state,
			paymentSplits: [...(state.paymentSplits ?? []), { payment: null, bank: TixBankTypes.UNKNOWN }],
		}));
	};

	const removePaymentSplit = (index: number) => {
		setOrderState((state) => ({
			...state,
			paymentSplits: state.paymentSplits?.filter((_paymentSplit, i) => i !== index),
		}));
	};

	const onChangePaymentSplit = (value: string | number | null, bank: TixBankTypes, index: number) => {
		const mappedValue = typeof value === 'number' ? value : !value ? null : new BigNumber(value).toNumber();
		setOrderState((state) => ({
			...state,
			paymentSplits: state.paymentSplits?.map((item, i) =>
				i === index ? { payment: mappedValue, bank: bank } : item,
			),
		}));
	};

	const addSeat = () => {
		setSeatsState((state) => [
			...state,
			{
				section: '',
				seat: '',
				row: '',
				noteAisle: false,
				noteRV: false,
				noteSide: false,
				note: '',
			},
		]);
	};

	const removeSeat = (index: number) => {
		setSeatsState((state) =>
			state.filter((seat, i) => {
				if (i !== index) return true;
				if (seat.id) setDeletedSeats((deletedSeats) => _.uniq([...deletedSeats, seat.id as string]));
				return false;
			}),
		);
	};

	const onChangeSeat = (key: keyof TixBought, value: string | boolean, index: number) => {
		setSeatsState((state) => state.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
	};

	const onClickSave = async () => {
		try {
			setSaving(true);
			const isSaved = await editOrderAPI({
				orderId: orderId,
				originalEvent: event,
				updatedEvent: {
					event: eventState.event,
					date: eventState.date,
					city: eventState.city,
					region: eventState.region,
				},
				updatedOrder: {
					email: orderState.email,
					reference: orderState.reference,
					primary: orderState.primary as TixPrimaryTypes,
					paymentSplits: orderState.paymentSplits,
					paymentDate: orderState.paymentDate,
					paymentTotal: orderState.paymentSplits.some((split) => split.payment === null)
						? null
						: orderState.paymentSplits
								.reduce(
									(total: BigNumber, paymentSplit) =>
										paymentSplit.payment !== null ? total.plus(paymentSplit.payment) : total,
									new BigNumber(0),
								)
								.toNumber(),
					paymentNative:
						orderState.paymentNative && orderState.paymentNativeCurrency
							? new BigNumber(orderState.paymentNative).toNumber()
							: null,
					paymentNativeCurrency:
						orderState.paymentNative && orderState.paymentNativeCurrency
							? orderState.paymentNativeCurrency
							: null,
				},
				updatedSeats: seatsState,
				deletedSeats: deletedSeats,
			});

			if (isSaved) {
				toast.success(`Order ${order.reference} edited!`);
				closeModal();
			}
		} catch (err) {
			console.error(err);
		} finally {
			setSaving(false);
		}
	};

	const onClickDeleteOrder = async () => {
		setSaving(true);
		const seatIds = Object.values(event.seats)
			.filter((seat) => seat.orderId === orderId)
			.map((seat) => seat.id);
		const isDeleted = await deleteOrderAPI({ params: { orderId: orderId } });
		if (isDeleted?.success) {
			onOrderDeleted(event.id, orderId, seatIds);
			toast.success(`Order ${order.reference} deleted!`);
			closeModal();
		} else {
			toast.error(isDeleted?.error ?? 'Failed to delete order!');
			setSaving(false);
		}
	};

	const checkInputs = () => {
		if (!eventState.event) return false;
		if (!eventState.date) return false;
		if (!eventState.city) return false;
		if (!eventState.region) return false;
		if (!orderState.email) return false;
		if (!orderState.reference) return false;
		if (!orderState.primary) return false;
		if (seatsState.some((seat) => !seat.section)) return false;
		return true;
	};

	return (
		<Modal title="Edit Order" closeModal={closeModal} style={{ maxWidth: 800 }}>
			<FormRow>
				<GridRow style={{ gridTemplateColumns: '1fr 1px 1fr', columnGap: '1.5rem' }}>
					<FlexCol>
						<FormRow>
							<Text $color="text_desc_alt" $size="1.6rem" $weight="500">
								Event Details
							</Text>
						</FormRow>
						<FormRow>
							<InputRow label="Event">
								<Input
									value={eventState.event}
									onChange={(v) => onChangeEvent('event', v)}
									placeholder="Raye"
								/>
							</InputRow>
							<InputRow label="Event Date">
								<Input
									value={eventState.date}
									onChange={(v) => onChangeEvent('date', v)}
									placeholder="Raye"
								/>
							</InputRow>
						</FormRow>
						<FormRow>
							<InputRow label="Event Region">
								<Select
									value={eventState.region}
									onChange={(v) => onChangeEvent('region', v)}
									options={getOptionList(
										'Region',
										convertEnumsToOptions(TixRegionTypes, TixRegionTypes),
									)}
								/>
							</InputRow>
							<InputRow label="Event City">
								<Input
									value={eventState.city}
									onChange={(v) => onChangeEvent('city', v)}
									placeholder="Raye"
								/>
							</InputRow>
						</FormRow>

						<FormRow style={{ marginTop: '1.5rem' }}>
							<Text $color="text_desc_alt" $size="1.6rem" $weight="500">
								Order Details
							</Text>
						</FormRow>
						<FormRow>
							<InputRow label="Order Email">
								<Input
									value={orderState.email}
									onChange={(v) => onChangeOrder('email', v)}
									placeholder="hello@world.com"
								/>
							</InputRow>
						</FormRow>
						<FormRow $columns="1fr 2fr">
							<InputRow label="Primary">
								<Select
									value={orderState.primary}
									onChange={(v) => onChangeOrder('primary', v)}
									options={getOptionList(
										'Primary',
										convertEnumsToOptions(TixPrimaryTypes, TixPrimaryTypes),
									)}
								/>
							</InputRow>
							<InputRow label="Reference">
								<Input
									value={orderState.reference}
									onChange={(v) => onChangeOrder('reference', v)}
									placeholder="# 123456/UK1"
								/>
							</InputRow>
						</FormRow>
						<FormRow>
							<InputRow label="Payment Date">
								<Input
									value={orderState.paymentDate}
									onChange={(v) => onChangeOrder('paymentDate', v)}
									placeholder="DD-MM-YYYY"
									onChangeMap={checkDateSchemaFormatPartly}
									onBlurMap={checkDateSchemaFormatFull}
								/>
							</InputRow>
						</FormRow>
						<FormRow $columns="1fr 2fr">
							<InputRow label="OG Currency (Opt.)">
								<Select
									value={orderState.paymentNativeCurrency ?? ''}
									onChange={(v) => onChangeOrder('paymentNativeCurrency', v)}
									options={getOptionList(
										'Currency',
										convertEnumsToOptions(TixCurrencyTypes, TixCurrencyTypes),
									)}
								/>
							</InputRow>
							<InputRow label="OG Amount (Opt.)">
								<Input
									value={orderState.paymentNative?.toString() ?? ''}
									onChange={(v) => onChangeOrder('paymentNative', v)}
									placeholder="12.34"
									onChangeMap={checkCurrencyFormatPartly}
									onBlurMap={checkCurrencyFormatFull}
								/>
							</InputRow>
						</FormRow>

						<FormRow style={{ marginTop: '1.5rem' }}>
							<Text $color="text_desc_alt" $size="1.6rem" $weight="500">
								Payment Splits
							</Text>
						</FormRow>

						{orderState.paymentSplits?.map((paymentSplit, index) => {
							return (
								<FormRow key={`edit_order_paymentsplit_${index}`}>
									<InputRow label="Bank">
										<Select
											value={orderState.paymentSplits[index].bank?.toString() ?? ''}
											onChange={(v) =>
												onChangePaymentSplit(paymentSplit.payment, v as TixBankTypes, index)
											}
											options={convertEnumsToOptions(TixBankTypes, TixBankTypes)}
										/>
									</InputRow>
									<InputRow label="Amount">
										<Input
											value={orderState.paymentSplits[index].payment?.toString() ?? ''}
											onChange={(v) => onChangePaymentSplit(v, paymentSplit.bank, index)}
											placeholder="12.34"
											onChangeMap={checkCurrencyFormatPartly}
											onBlurMap={checkCurrencyFormatFull}
										/>
									</InputRow>
									<ButtonRed
										onClick={() => removePaymentSplit(index)}
										style={{ alignSelf: 'flex-end' }}
									>
										Remove
									</ButtonRed>
								</FormRow>
							);
						})}

						<FormRow>
							<ButtonGrey onClick={() => addPaymentSplit()}>Add Payment Split</ButtonGrey>
						</FormRow>
					</FlexCol>
					<StyledDivider />
					<FlexCol>
						<FormRow>
							<Text $color="text_desc_alt" $size="1.6rem" $weight="500">
								Seats
							</Text>
						</FormRow>
						{seatsState?.map((_seat, index) => {
							return (
								<StyledSeatRow key={`edit_order_seat_${index}`} $columns="1fr 75px">
									<StyledSeatInfoContainer>
										<FormRow>
											<InputRow label="Section">
												<Input
													value={seatsState[index].section}
													onChange={(v) => onChangeSeat('section', v, index)}
													placeholder="Stalls"
												/>
											</InputRow>
											<InputRow label="Row">
												<Input
													value={seatsState[index].row}
													onChange={(v) => onChangeSeat('row', v, index)}
													placeholder="B"
												/>
											</InputRow>
											<InputRow label="Seat">
												<Input
													value={seatsState[index].seat}
													onChange={(v) => onChangeSeat('seat', v, index)}
													placeholder="12"
												/>
											</InputRow>
										</FormRow>
										<FormRow>
											<ButtonRed onClick={() => removeSeat(index)}>Remove Seat</ButtonRed>
										</FormRow>
									</StyledSeatInfoContainer>
									<StyledSeatPropsContainer>
										<InputCol label="Aisle" reverse={true}>
											<Checkbox
												value={seatsState[index].noteAisle}
												onChange={(v) => onChangeSeat('noteAisle', !v ? false : true, index)}
											/>
										</InputCol>
										<InputCol label="Side" reverse={true}>
											<Checkbox
												value={seatsState[index].noteSide}
												onChange={(v) => onChangeSeat('noteSide', !v ? false : true, index)}
											/>
										</InputCol>
										<InputCol label="RV" reverse={true}>
											<Checkbox
												value={seatsState[index].noteRV}
												onChange={(v) => onChangeSeat('noteRV', !v ? false : true, index)}
											/>
										</InputCol>
									</StyledSeatPropsContainer>
								</StyledSeatRow>
							);
						})}

						<FormRow>
							<ButtonGreen onClick={() => addSeat()}>Add Seat</ButtonGreen>
						</FormRow>
					</FlexCol>
				</GridRow>
			</FormRow>
			<FormRow $columns="2fr 2fr 1fr">
				<ButtonGrey onClick={closeModal}>Cancel</ButtonGrey>
				<ButtonGreen disabled={!checkInputs() || saving} onClick={onClickSave}>
					Save
				</ButtonGreen>
				<ButtonRed onClick={onClickDeleteOrder}>DELETE ORDER</ButtonRed>
			</FormRow>
		</Modal>
	);
}
